import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useMemo, useEffect, useState } from 'react';
import { formatNumberToCurrency } from '@/utils/currency';
import dayjs from '@/config/dayjs';
import {
  DataGridPremium,
  GRID_AGGREGATION_FUNCTIONS,
  GridAggregationFunction,
  GridAggregationModel,
  GridApiPremium,
  GridColDef,
  GridColumnVisibilityModel,
  type GridGroupNode,
  GridRenderCellParams,
  type GridListViewColDef,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from '@mui/x-data-grid-premium';
import { defaultToolbarExportSlotProps } from '@/components/table/toolbarSlotProps';
import { useGetHoldingsHistoricalTrendQuery } from '@/store/holdingsApi';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useAccountDetailsFlyout } from '@/hooks/useAccountDetailsFlyout';
import { useLocalization } from '@/hooks/useLocalization';
import CustomToolbarForNative from '@/components/CustomToolbarForNative';
import { Capacitor } from '@capacitor/core';
import XsPreferencesDrawerToolbar from '@/components/table/XsPreferencesDrawerToolbar';
import { ManagedColumnsManagement } from '@/components/table/ManagedColumnsManagement';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useGetAccountsQuery } from '@/store/accountsApi';
import { AccountBase } from '@/models/AccountViewModel';
import {
  HoldingsQueryParams,
  HoldingsTrendDataPoint,
  HoldingViewModel,
  useGetHoldingsQuery,
} from '@/store/holdingsApi';
import { GRID_COLUMN_WIDTH_NARROW } from '@/constants/dimensions';
import { useViewingTagSelection } from '@/hooks/useViewingTagSelection';
import { filterByInScopeIds } from '@/utils/historicalAccountScope';
import { sumKnownHistoricalAmounts } from '@/utils/historicalBalance';
import { GridContainer } from '../../../components/grids/GridContainer';

export interface HoldingsHistoricalTableProps {
  startDate?: string;
  endDate?: string;
  isWidget: boolean;
  groupBy?: string;
  onDataLoaded?: () => void;
  excludedAccounts?: number[];
  excludedAccountTypes?: number[];
  excludedCategories?: number[];
}

type HoldingsHistoricalRow = {
  id: string | number;
  name: string;
  startValue: number | null;
  endValue: number | null;
  dollarChange: number | null;
  percentChange: number | null;
  AccountName?: string;
  AccountId?: number;
  InstitutionName?: string;
  CustomGroup?: string;
};

// Per-cell value produced by the percent-change aggregation's getCellValue.
type PriceChangeAggValue = {
  startValue: number | null;
  dollarChange: number | null;
};

// HoldingsTrendItem types id/name/data as always-present, but the trend API can
// return malformed records, so widen here to keep the runtime guards honest.
type LooseTrendItem = {
  id?: number | null;
  name?: string | null;
  data?: HoldingsTrendDataPoint[] | null;
};

/** Default column visibility — grouping helper columns start hidden. */
const HOLDINGS_HISTORICAL_DEFAULT_COLUMN_VISIBILITY: GridColumnVisibilityModel =
  {
    CustomGroup: false,
    AccountName: false,
  };

/**
 * Group sum that stays blank when every member value is missing.
 * MUI's built-in sum returns `$0` for an all-null group.
 */
const knownSumAggregation: GridAggregationFunction<
  number | null,
  number | null
> = {
  label: '',
  columnTypes: ['number'],
  apply: ({ values }) =>
    sumKnownHistoricalAmounts(
      values.map((value) => (typeof value === 'number' ? value : null)),
    ),
};

const HoldingsHistoricalTable: React.FC<HoldingsHistoricalTableProps> = ({
  startDate,
  endDate,
  isWidget,
  groupBy = 'none',
  onDataLoaded,
  excludedAccounts,
  excludedAccountTypes,
  excludedCategories,
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const authLoaded = useAuthIsLoaded();
  const apiRef = useGridApiRef();
  const { t } = useLocalization();
  const {
    openAccountById,
    openAccountByName,
    canOpenAccount,
    canOpenAccountByName,
    accountFlyout,
  } = useAccountDetailsFlyout();
  const notAvailableLabel = t('common.emdash', '—');
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(
      HOLDINGS_HISTORICAL_DEFAULT_COLUMN_VISIBILITY,
    );

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- guard Capacitor.getPlatform being absent on non-Capacitor runtimes (typed non-nullable but can be undefined when the shim isn't initialized)
  const isNative = Capacitor.getPlatform && Capacitor.getPlatform() !== 'web';

  // Prepare query params
  const params = useMemo(() => {
    if (!startDate || !endDate) return null;

    return {
      startDate,
      endDate,
      page: 1,
      limit: 50000,
      ...(excludedAccounts?.length
        ? { ExcludedAccounts: excludedAccounts.join(',') }
        : {}),
      ...(excludedAccountTypes?.length
        ? { ExcludedAccountTypes: excludedAccountTypes.join(',') }
        : {}),
      ...(excludedCategories?.length
        ? { ExcludedCategories: excludedCategories.join(',') }
        : {}),
    };
  }, [
    startDate,
    endDate,
    excludedAccounts,
    excludedAccountTypes,
    excludedCategories,
  ]);

  // Use RTK Query to fetch historical holdings trend data
  const {
    data: holdingsData,
    isLoading,
    isFetching,
    isError,
  } = useGetHoldingsHistoricalTrendQuery(
    authLoaded && params ? params : skipToken,
  );

  const holdingsParams = useMemo(() => {
    if (!endDate) return null;
    const p: HoldingsQueryParams = {
      searchTerm: '',
      page: 1,
      asOfDate: endDate,
      ...(excludedAccounts?.length
        ? { ExcludedAccounts: excludedAccounts.join(',') }
        : {}),
      ...(excludedAccountTypes?.length
        ? { ExcludedAccountTypes: excludedAccountTypes.join(',') }
        : {}),
      ...(excludedCategories?.length
        ? { ExcludedCategories: excludedCategories.join(',') }
        : {}),
    };
    return p;
  }, [endDate, excludedAccounts, excludedAccountTypes, excludedCategories]);

  const {
    data: holdingsSnapshot,
    isSuccess: holdingsSnapshotReady,
    isLoading: isSnapshotLoading,
    isFetching: isSnapshotFetching,
  } = useGetHoldingsQuery(
    authLoaded && holdingsParams ? holdingsParams : skipToken,
  );

  const viewingTag = useViewingTagSelection();

  const { data: accountsData } = useGetAccountsQuery(
    authLoaded && endDate
      ? {
          showClosed: false,
          asOfDate: endDate,
          ...(excludedAccounts?.length
            ? { ExcludedAccounts: excludedAccounts }
            : {}),
          ...(excludedAccountTypes?.length
            ? { ExcludedAccountTypes: excludedAccountTypes }
            : {}),
          ...(excludedCategories?.length
            ? { ExcludedCategories: excludedCategories }
            : {}),
        }
      : skipToken,
  );

  const accountTagMap = useMemo(() => {
    // Keyed only by accounts present in the response, so a lookup for an
    // unrelated accountId can be undefined — reflect that in the type so the
    // `?.` guard at the call site stays honest.
    const map: Record<number, Record<string, string> | undefined> = {};
    // `useGetAccountsQuery`'s `data` is loosely typed (any) at this call site, so
    // narrow it to its real response shape before iterating.
    const accounts = accountsData;
    accounts?.Records.forEach((account: AccountBase) => {
      // `Tags` is typed as always-present but the API may omit it; keep the fallback.
      const tags = account.Tags as Record<string, string> | undefined;
      map[account.AccountId] = tags ?? {};
    });
    return map;
  }, [accountsData]);

  const holdingMetaById = useMemo(() => {
    const map = new Map<
      number,
      { AccountId: number; AccountName: string; InstitutionName: string }
    >();
    // `useGetHoldingsQuery`'s `data` is loosely typed (any) at this call site, so
    // narrow it to its real response shape before iterating.
    const snapshot = holdingsSnapshot as
      | { Records: HoldingViewModel[] }
      | undefined;
    snapshot?.Records.forEach((h: HoldingViewModel) => {
      map.set(h.HoldingId, {
        AccountId: h.AccountId,
        AccountName: h.AccountName,
        InstitutionName: h.InstitutionName,
      });
    });
    return map;
  }, [holdingsSnapshot]);

  const inScopeHoldingIds = useMemo(() => {
    if (viewingTag === null || !holdingsSnapshotReady) {
      return undefined;
    }
    return new Set(holdingMetaById.keys());
  }, [holdingMetaById, holdingsSnapshotReady, viewingTag]);

  useEffect(() => {
    if (holdingsData?.Records) {
      onDataLoaded?.();
    }
  }, [holdingsData, onDataLoaded]);

  // Find start and end dates from data
  const startDateValue = useMemo(() => {
    if (!holdingsData?.Records || holdingsData.Records.length === 0)
      return null;

    // Find the earliest date across all holdings. The trend API can return a
    // record with missing/null data, so guard before dereferencing .length.
    const firstRecord = (holdingsData.Records as LooseTrendItem[])[0];
    if (!firstRecord.data || firstRecord.data.length === 0) return null;

    return dayjs(firstRecord.data[0].Date).format('MM/DD/YYYY');
  }, [holdingsData]);

  const endDateValue = useMemo(() => {
    if (!holdingsData?.Records || holdingsData.Records.length === 0)
      return null;

    // Find the latest date across all holdings. The trend API can return a
    // record with missing/null data, so guard before dereferencing .length.
    const firstRecord = (holdingsData.Records as LooseTrendItem[])[0];
    if (!firstRecord.data || firstRecord.data.length === 0) return null;

    return dayjs(firstRecord.data[firstRecord.data.length - 1].Date).format(
      'MM/DD/YYYY',
    );
  }, [holdingsData]);

  // Prepare data for DataGrid
  const rows: HoldingsHistoricalRow[] = useMemo(() => {
    if (!holdingsData?.Records) return [];

    const records = holdingsData.Records as LooseTrendItem[];

    return filterByInScopeIds(
      records,
      (holding) => holding.id,
      inScopeHoldingIds,
    )
      .filter(
        (
          holding,
        ): holding is {
          id: number;
          name: string;
          data: HoldingsTrendDataPoint[];
        } =>
          holding.id != null &&
          (holding.name ?? '').trim() !== '' &&
          Array.isArray(holding.data) &&
          holding.data.length > 0,
      )
      .map((holding) => {
        const meta = holdingMetaById.get(holding.id);
        const startDataPoint = holding.data.find((point) =>
          dayjs(point.Date).isSame(dayjs(startDate), 'day'),
        );
        const lastDataPoint = holding.data[holding.data.length - 1];

        // Calculate change values
        const startValue = startDataPoint?.Value ?? null;
        const endValue =
          typeof lastDataPoint.Value === 'number' ? lastDataPoint.Value : null;
        const dollarChange =
          startValue == null || endValue == null ? null : endValue - startValue;
        const percentChange =
          startValue == null || dollarChange == null
            ? null
            : startValue === 0
              ? dollarChange === 0
                ? 0
                : null
              : (dollarChange / startValue) * 100;

        const notSetLabel = t('notSet', 'Not Set');
        const accountName = meta?.AccountName ?? notSetLabel;
        const accountId = meta?.AccountId;

        const customGroupValue =
          groupBy !== 'none' && groupBy !== 'account' && accountId != null
            ? accountTagMap[accountId]?.[groupBy]
            : undefined;

        return {
          id: holding.id,
          name: holding.name,
          startValue,
          endValue,
          dollarChange,
          percentChange,
          AccountId: accountId,
          AccountName: accountName,
          InstitutionName: meta?.InstitutionName,
          // Always provide a grouping value when grouping is enabled, so rows don't "float" outside groups.
          CustomGroup:
            groupBy !== 'none' && groupBy !== 'account'
              ? (customGroupValue ?? notSetLabel)
              : undefined,
        };
      });
  }, [
    holdingsData,
    holdingMetaById,
    inScopeHoldingIds,
    groupBy,
    accountTagMap,
    t,
    startDate,
  ]);

  // Calculate total
  const total = useMemo(() => {
    const knownEnds = rows
      .map((item) => item.endValue)
      .filter((value): value is number => typeof value === 'number');
    if (knownEnds.length === 0) return null;
    return knownEnds.reduce((acc, value) => acc + value, 0);
  }, [rows]);

  const priceChangePercentAggregation = useMemo<
    GridAggregationFunction<PriceChangeAggValue, number | null>
  >(
    () => ({
      label: '',
      getCellValue: ({ row }) => {
        const holdingRow = row as Partial<HoldingsHistoricalRow>;
        return {
          startValue:
            typeof holdingRow.startValue === 'number'
              ? holdingRow.startValue
              : null,
          dollarChange:
            typeof holdingRow.dollarChange === 'number'
              ? holdingRow.dollarChange
              : null,
        };
      },
      apply: (params: { values: (PriceChangeAggValue | undefined)[] }) => {
        let sawComplete = false;
        let startValue = 0;
        let dollarChange = 0;

        for (const value of params.values) {
          // A known start with a missing end must not enter the denominator.
          if (
            value == null ||
            typeof value.startValue !== 'number' ||
            typeof value.dollarChange !== 'number'
          ) {
            continue;
          }

          startValue += value.startValue;
          dollarChange += value.dollarChange;
          sawComplete = true;
        }

        if (!sawComplete) return null;
        if (startValue === 0) {
          return dollarChange === 0 ? 0 : null;
        }

        return (dollarChange / startValue) * 100;
      },
    }),
    [],
  );

  // Define columns for DataGrid
  const columns: GridColDef<HoldingsHistoricalRow>[] = [
    {
      field: 'AccountName',
      headerName: t('grid.accountName', 'Account'),
      flex: 1,
      minWidth: GRID_COLUMN_WIDTH_NARROW,
      // EUD-440: clicking the account name opens the Account Details flyout.
      renderCell: (params: GridRenderCellParams<HoldingsHistoricalRow>) => {
        // When grouping by account, the grid delegates the group-header value to
        // this renderCell; group nodes have no AccountName field, so render the
        // grouping key (the account name) plainly instead of blanking the header.
        if (params.rowNode.type === 'group') {
          // This is the AccountName column; it only has a value for account
          // groups. For any other grouping field the group row's cell here is
          // empty so it doesn't echo the other group's key (EUD-440).
          if (params.rowNode.groupingField !== 'AccountName') {
            return null;
          }
          const key = params.rowNode.groupingKey;
          if (key == null) return null;
          const label = String(key);
          if (!canOpenAccountByName(label)) {
            return <Typography variant='body2'>{label}</Typography>;
          }
          return (
            <Typography
              variant='body2'
              onClick={(event) => {
                event.stopPropagation();
                openAccountByName(label);
              }}
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              {label}
            </Typography>
          );
        }
        const name = params.row.AccountName ?? '';
        if (!name) return null;
        if (!canOpenAccount(params.row.AccountId)) {
          return <Typography variant='body2'>{name}</Typography>;
        }
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              cursor: 'pointer',
            }}
            onClick={() => {
              openAccountById(params.row.AccountId);
            }}
          >
            <Typography
              variant='body2'
              sx={{ '&:hover': { textDecoration: 'underline' } }}
            >
              {name}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'CustomGroup',
      headerName: t('grid.group', 'Group'),
      flex: 1,
      minWidth: GRID_COLUMN_WIDTH_NARROW,
    },
    {
      field: 'name',
      headerName: t('name'),
      flex: 1,
      minWidth: GRID_COLUMN_WIDTH_NARROW,
      maxWidth: 300,
      cellClassName: 'holdings-name-cell',
      // EUD-537: match the main Holdings grid — holding name on the primary line
      // with "Institution: Account" as a subtitle line in the same cell.
      renderCell: (params: GridRenderCellParams<HoldingsHistoricalRow>) => {
        const institution = params.row.InstitutionName;
        const account = params.row.AccountName ?? '';
        const accountId = params.row.AccountId;
        const hasSubtitle = Boolean(institution) || Boolean(account);
        return (
          <Box
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              width: '100%',
              lineHeight: 1.2,
              py: 0,
            }}
          >
            <Typography
              variant='body2'
              color='text.primary'
              sx={{
                fontWeight: 400,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}
            >
              {params.value}
            </Typography>
            {hasSubtitle && (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{
                  fontWeight: 400,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                {/* EUD-440: AccountName column is hidden in the default view, so
                    this subtitle is the visible account name — make it open the
                    Account Details flyout. */}
                {institution ? `${institution}: ` : ''}
                {account && canOpenAccount(accountId) ? (
                  <Box
                    component='span'
                    onClick={(event) => {
                      event.stopPropagation();
                      openAccountById(accountId);
                    }}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {account}
                  </Box>
                ) : (
                  account
                )}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'startValue',
      headerName: `${startDateValue || t('date')} Value`,
      type: 'number',
      availableAggregationFunctions: ['sum'],
      renderCell: (params) => {
        if (params.value == null) return notAvailableLabel;
        return formatNumberToCurrency(Number(params.value));
      },
      width: GRID_COLUMN_WIDTH_NARROW,
    },
    {
      field: 'endValue',
      headerName: `${endDateValue || t('date')} Value`,
      type: 'number',
      aggregable: false,
      renderCell: (params) => {
        if (params.value == null) return notAvailableLabel;
        return formatNumberToCurrency(Number(params.value));
      },
      width: GRID_COLUMN_WIDTH_NARROW,
    },
    {
      field: 'dollarChange',
      headerName: t('grid.dollarChange', '$ Change'),
      type: 'number',
      availableAggregationFunctions: ['sum'],
      renderCell: (params) => {
        if (params.value == null) return notAvailableLabel;
        const value = Number(params.value);
        const color = value >= 0 ? 'success.main' : 'error.main';
        return (
          <Typography sx={{ color }}>
            {formatNumberToCurrency(value)}
          </Typography>
        );
      },
      width: GRID_COLUMN_WIDTH_NARROW,
    },
    {
      field: 'percentChange',
      headerName: t('grid.percentChange', '% Change'),
      type: 'number',
      availableAggregationFunctions: ['priceChangePercent'],
      renderCell: (params) => {
        if (params.value == null) return notAvailableLabel;
        const value = Number(params.value);
        const color = value >= 0 ? 'success.main' : 'error.main';
        return <Typography sx={{ color }}>{value.toFixed(2)}%</Typography>;
      },
      width: 120,
    },
  ];

  const groupingModel = useMemo(() => {
    if (groupBy === 'account') return ['AccountName'];
    if (groupBy !== 'none') return ['CustomGroup'];
    return [];
  }, [groupBy]);

  const aggregationModel = useMemo<GridAggregationModel>(() => {
    if (groupBy === 'none') return {};

    const model: GridAggregationModel = {
      startValue: 'sum',
      dollarChange: 'sum',
      percentChange: 'priceChangePercent',
    };
    return model;
  }, [groupBy]);

  const aggregationFunctions = useMemo(
    () => ({
      ...GRID_AGGREGATION_FUNCTIONS,
      sum: knownSumAggregation,
      priceChangePercent: priceChangePercentAggregation,
    }),
    [priceChangePercentAggregation],
  );

  useEffect(() => {
    setColumnVisibilityModel((current) => ({
      ...current,
      // EUD-537: account + institution now live in the Name cell subtitle, so the
      // standalone Account and Group columns stay hidden in every view. They remain
      // defined so account/tag grouping can still resolve their fields. Re-applied
      // on groupBy change since useKeepGroupedColumnsHidden can restore visibility.
      CustomGroup: false,
      AccountName: false,
    }));
  }, [groupBy]);

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    rowGroupingModel: groupingModel,
    initialState: {
      rowGrouping: { model: groupingModel },
    },
  });

  const listViewColDef: GridListViewColDef<HoldingsHistoricalRow> = useMemo(
    () => ({
      field: 'listColumn',
      renderCell: (params: GridRenderCellParams<HoldingsHistoricalRow>) => {
        const { row, rowNode, api } = params;

        // Group header row when grouping is enabled
        if (rowNode.type === 'group') {
          // `rowNode.type === 'group'` narrows rowNode to GridGroupNode.
          const rawKey = rowNode.groupingKey;
          // groupingKey is string | number | null; coerce a real key to a string
          // (never Object's "[object Object]") and fall back to "Not Set".
          const keyString = rawKey == null ? '' : String(rawKey);
          const groupKey =
            keyString.trim() !== '' ? keyString : t('notSet', 'Not Set');

          const isExpanded = !!rowNode.childrenExpanded;

          const handleToggleExpand = (event: React.MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            (api as GridApiPremium).setRowChildrenExpansion(
              params.id,
              !isExpanded,
            );
          };

          // Single-level grouping, so the direct children count is the count shown.
          const descendantCount = rowNode.children.length;

          // Inline group aggregation result (attached by MuiX; not in the public node type).
          const aggregation = (
            rowNode as GridGroupNode & {
              aggregation?: { endValue?: { sum?: number } };
            }
          ).aggregation;

          const sumEndValue = aggregation?.endValue?.sum;

          return (
            <Box
              sx={{
                width: '100%',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  minWidth: 0,
                }}
              >
                <Box
                  onClick={handleToggleExpand}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    p: 0.25,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.action.hover,
                    },
                  }}
                >
                  {isExpanded ? (
                    <ExpandMoreIcon fontSize='small' />
                  ) : (
                    <ChevronRightIcon fontSize='small' />
                  )}
                </Box>

                <Typography
                  variant='body1'
                  sx={{
                    fontWeight: '600 !important',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {rowNode.groupingField === 'AccountName' &&
                  canOpenAccountByName(groupKey) ? (
                    <Box
                      component='span'
                      onClick={(event) => {
                        event.stopPropagation();
                        openAccountByName(groupKey);
                      }}
                      sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      {groupKey}
                    </Box>
                  ) : (
                    groupKey
                  )}
                  {descendantCount > 0 ? ` (${String(descendantCount)})` : ''}
                </Typography>
              </Box>

              {sumEndValue != null ? (
                <Typography
                  variant='body1'
                  sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                  {formatNumberToCurrency(sumEndValue)}
                </Typography>
              ) : null}
            </Box>
          );
        }

        const visibilityModel = api.state.columns.columnVisibilityModel;
        const isFieldVisible = (field: string) => {
          // Index access is typed `boolean` but is `undefined` for columns absent
          // from the model; widen so a missing key still maps to "visible".
          const visible = visibilityModel[field] as boolean | undefined;
          return visible !== false;
        };

        const name = row.name.trim();
        const startValue = row.startValue;
        const endValue = row.endValue;
        const dollarChange = row.dollarChange;
        const percentChange = row.percentChange;

        const startLabel = `${startDateValue || t('date')} ${t(
          'grid.balance',
          'Value',
        )}`;
        const endLabel = `${endDateValue || t('date')} ${t(
          'grid.balance',
          'Value',
        )}`;

        const getValueColor = (v: number) => {
          if (v > 0) return 'success.main';
          if (v < 0) return 'error.main';
          return 'text.secondary';
        };

        const Line = (line: {
          left: string;
          right: string;
          rightSx?: object;
        }) => (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography
              variant='body2'
              sx={{ flex: 1, minWidth: 0, color: 'text.secondary' }}
            >
              {line.left}
            </Typography>
            <Typography
              variant='body2'
              sx={{
                whiteSpace: 'nowrap',
                textAlign: 'right',
                color: 'text.secondary',
                ...line.rightSx,
              }}
            >
              {line.right}
            </Typography>
          </Box>
        );

        return (
          <Box
            sx={{
              width: '100%',
              px: 0,
              py: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
            }}
          >
            {/* Line 1: Holding Name + End Value */}
            {(isFieldVisible('name') || isFieldVisible('endValue')) && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                <Typography
                  variant='body1'
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                    fontWeight: '600 !important',
                  }}
                >
                  {isFieldVisible('name') ? name : ''}
                </Typography>
              </Box>
            )}

            {isFieldVisible('startValue') && (
              <Line
                left={startLabel}
                right={
                  startValue == null
                    ? notAvailableLabel
                    : formatNumberToCurrency(startValue)
                }
              />
            )}

            {isFieldVisible('endValue') && (
              <Line
                left={endLabel}
                right={
                  endValue == null
                    ? notAvailableLabel
                    : formatNumberToCurrency(endValue)
                }
              />
            )}

            {isFieldVisible('dollarChange') && (
              <Line
                left={t('grid.dollarChange', '$ Change')}
                right={
                  dollarChange == null
                    ? notAvailableLabel
                    : formatNumberToCurrency(dollarChange)
                }
                rightSx={{
                  color:
                    dollarChange == null
                      ? 'text.secondary'
                      : getValueColor(dollarChange),
                  fontWeight:
                    dollarChange != null && dollarChange !== 0
                      ? '600 !important'
                      : undefined,
                }}
              />
            )}

            {isFieldVisible('percentChange') && (
              <Line
                left={t('grid.percentChange', '% Change')}
                right={
                  percentChange == null
                    ? notAvailableLabel
                    : `${percentChange.toFixed(2)}%`
                }
                rightSx={{
                  color:
                    percentChange == null
                      ? 'text.secondary'
                      : getValueColor(percentChange),
                  fontWeight:
                    percentChange != null && percentChange !== 0
                      ? '600 !important'
                      : undefined,
                }}
              />
            )}
          </Box>
        );
      },
    }),
    [
      endDateValue,
      notAvailableLabel,
      startDateValue,
      t,
      canOpenAccountByName,
      openAccountByName,
    ],
  );

  if (isError) {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <Typography color='error'>
          {t(
            'errorLoadingHistoricalHoldingsData',
            'Error loading historical holdings data.',
          )}
        </Typography>
      </Box>
    );
  }

  if (
    (!holdingsData?.Records || holdingsData.Records.length === 0) &&
    !isLoading &&
    !isFetching &&
    authLoaded
  ) {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <Typography>
          {t(
            'noHistoricalHoldingsDataAvailable',
            'No historical holdings data available.',
          )}
        </Typography>
      </Box>
    );
  }
  return (
    <Box>
      <Box
        sx={{
          width: '100%',
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-cell--textLeft': {
            justifyContent: 'flex-start',
          },
          '& .MuiDataGrid-cell--textRight': {
            justifyContent: 'flex-end',
          },
          ...(isXs && {
            '& .MuiDataGrid-row:not(.MuiDataGrid-row--group) .MuiDataGrid-cellEmpty':
              {
                display: 'none !important',
              },
            '& .MuiDataGrid-main': { width: '100%' },
            '& .MuiDataGrid-virtualScroller': { width: '100%' },
            '& .MuiDataGrid-virtualScrollerRenderZone': {
              width: '100% !important',
              minWidth: '100% !important',
              maxWidth: '100% !important',
            },
            '& .MuiDataGrid-virtualScrollerContent': {
              width: '100% !important',
            },
            '& .MuiDataGrid-virtualScrollerContent > div': {
              width: '100% !important',
              maxWidth: '100% !important',
            },
            '& .MuiDataGrid-row': {
              display: 'flex',
              flexWrap: 'nowrap',
              width: '100% !important',
              minWidth: '100% !important',
              maxWidth: '100% !important',
            },
            '& .MuiDataGrid-cell': {
              display: 'flex',
              flexWrap: 'nowrap',
              width: '100% !important',
              minWidth: '100% !important',
              maxWidth: '100% !important',
              flex: '1 1 0',
            },
            '& .MuiDataGrid-cellContent': {
              width: '100%',
              flex: 1,
              minWidth: 0,
            },
          }),
        }}
      >
        <GridContainer isWidget={isWidget}>
          <DataGridPremium
            key={
              isXs ? `holdings-historical-xs-${groupBy}` : 'holdings-historical'
            }
            rows={rows}
            columns={columns}
            apiRef={apiRef}
            disableRowSelectionOnClick
            showToolbar
            disablePivoting
            listView={isXs}
            {...(isXs ? { listViewColumn: listViewColDef } : {})}
            getRowHeight={() => 'auto'}
            // Estimate only matters for the bounded widget case (where the grid
            // virtualizes); autoHeight renders every row at its measured height.
            getEstimatedRowHeight={() => 56}
            loading={
              isLoading ||
              isFetching ||
              !authLoaded ||
              (viewingTag !== null &&
                (isSnapshotLoading === true ||
                  isSnapshotFetching === true ||
                  holdingsSnapshotReady !== true))
            }
            initialState={initialState}
            rowGroupingModel={groupingModel}
            defaultGroupingExpansionDepth={groupBy === 'none' ? 0 : -1}
            aggregationModel={aggregationModel}
            aggregationFunctions={aggregationFunctions}
            aggregationRowsScope='all'
            // Prevent the DataGrid from creating a pinned "footer" aggregation row (shows as a pinned row in list view).
            // We only want aggregation inline for group rows, matching the main Holdings grid behavior.

            getAggregationPosition={(groupNode: GridGroupNode | null) =>
              groupNode == null ? null : 'inline'
            }
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            slotProps={{
              loadingOverlay: {
                variant: 'linear-progress',
                noRowsVariant: 'skeleton',
              },
              // Hide grouping helper columns from the Columns drawer on XS.
              columnsManagement: {
                defaultColumnVisibilityModel:
                  HOLDINGS_HISTORICAL_DEFAULT_COLUMN_VISIBILITY,
                getTogglableColumns: (cols: Array<{ field: string }>) =>
                  cols
                    .map((c) => c.field || '')
                    .filter(
                      (field) =>
                        field !== 'AccountName' &&
                        field !== 'CustomGroup' &&
                        field !== 'actions' &&
                        field !== 'listColumn' &&
                        field !== '__list__' &&
                        !field.startsWith('__'),
                    ),
              },
              ...(isXs
                ? { toolbar: { showQuickFilter: true } }
                : {
                    toolbar: {
                      ...defaultToolbarExportSlotProps,
                      showQuickFilter: true,
                    },
                  }),
            }}
            slots={{
              columnsManagement: ManagedColumnsManagement,
              ...(isNative
                ? { toolbar: CustomToolbarForNative }
                : isXs
                  ? { toolbar: XsPreferencesDrawerToolbar }
                  : {}),
            }}
            sx={{
              // Widget: fill the bounded viewport so the grid's own scroller is the
              // single scrollbar. Full page uses autoHeight, so no fill needed.
              // ...(isWidget ? { flex: 1, minHeight: 0 } : {}),
              '& .MuiDataGrid-aggregationColumnHeaderLabel': {
                display: 'none',
              },
              '& .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
              },
            }}
          />
        </GridContainer>

        <Box
          sx={{
            mt: 2,
            fontWeight: 600,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant='subtitle1'>{t('total', 'Total')}</Typography>
          <Typography variant='subtitle1'>
            {total == null ? notAvailableLabel : formatNumberToCurrency(total)}
          </Typography>
        </Box>
      </Box>
      {accountFlyout}
    </Box>
  );
};

export default HoldingsHistoricalTable;
